import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { match, P } from 'ts-pattern';

import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { VersionedName } from '@/components/versionedName/versionedName';
import { useSchedulesList } from '@/hooks/queries/useEvaluations';
import { useOrgUsersMapQuery } from '@/hooks/queries/useOrg';
import { useSources } from '@/hooks/queries/useSources';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { OrgMember } from '@/types/auth';
import { ScheduleDisabledReason, ScheduleWithDetails, Source } from '@/types/evals';
import { formatCronCadence } from '@/utils/cronFormat';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './scheduleTable.module.scss';

const formatDisabledReason = ( reason: ScheduleDisabledReason | null ): string => match( reason )
  .with( { type: 'source_deprecated' }, () => 'Cannot run (source deprecated)' )
  .with( { type: 'evaluation_deprecated' }, () => 'Cannot run (evaluation deprecated)' )
  .with( { type: 'metric_provider_version_deprecated' }, () => 'Cannot run (metric deprecated)' )
  .with( { type: 'metric_invalid_llm_model' }, () => 'Cannot run (metric LLM unavailable)' )
  .with( { type: 'source_unavailable' }, () => 'Cannot run (source unavailable)' )
  .with( { type: 'source_version_unavailable' }, () => 'Cannot run (source version unavailable)' )
  .with( P.nullish, () => 'Disabled' )
  .exhaustive();

const formatNextRun = ( row: ScheduleWithDetails, useLocalZone: boolean ) => match( row.schedule.state )
  .with( 'PAUSED', () => 'Schedule paused' )
  .with( 'DISABLED', () => (
    <span className={ styles.disabled_next_run }>
      { formatDisabledReason( row.schedule.disabled_reason ) }
    </span>
  ) )
  .with( 'ACTIVE', () => (
    <TimeFormatter
      sourceType={ TimeSourceTypeEnum.ISO }
      value={ row.schedule.next_fire_at }
      opts={ { useLocalZone } }
      outputFormat={ OutputFormatEnum.STRING_SINGLE }
    />
  ) )
  .exhaustive();

const columnHelper = createColumnHelper<ScheduleWithDetails>();

type Props = {
  evaluationId: string;
};

const displayUser = ( userId: string | null | undefined, usersMap: Map<string, OrgMember> | undefined ): string => {
  if ( !userId ) return '—';
  const u = usersMap?.get( userId );
  return u?.name || u?.email || userId;
};

export function ScheduleTable( { evaluationId }: Props ) {
  const [ useLocalZone ] = useLocalZonePreference();
  const [ expanded, setExpanded ] = useState<Set<string>>( new Set() );
  const { data: schedules, isLoading, isError } = useSchedulesList(
    { evaluation_id: evaluationId, include_details: true, limit: 500 },
    { enabled: !!evaluationId }
  );
  const { data: sources } = useSources();
  const { data: usersMap } = useOrgUsersMapQuery();

  const sourcesById = useMemo( () => {
    const map = new Map<string, Source>();
    ( sources ?? [] ).forEach( s => map.set( s.id, s ) );
    return map;
  }, [ sources ] );

  const toggleExpanded = useCallback( ( id: string ) => {
    setExpanded( prev => {
      const next = new Set( prev );
      if ( next.has( id ) ) next.delete( id );
      else next.add( id );
      return next;
    } );
  }, [] );

  const columns = useMemo<ColumnDef<ScheduleWithDetails>[]>( () => [
    columnHelper.display( {
      id: 'source',
      header: 'Evaluate with Source',
      cell: info => {
        const { schedule } = info.row.original;
        const source = sourcesById.get( schedule.source_id );
        const name = source?.name ?? schedule.source_id;
        const version = schedule.source_version ?? source?.version;
        const isDeprecated = source?.is_deprecated;
        const isExpanded = expanded.has( schedule.id );
        return (
          <span className={ styles.source_cell }>
            <Icon icon={ isExpanded ? FiChevronDown : FiChevronRight } />
            <VersionedName name={ name } version={ version } isDeprecated={ isDeprecated } />
          </span>
        );
      },
      meta: { classStr: buildColumnClassString( { width: 35, styles: ColumnModEnums.NO_OVERFLOW } ) }
    } ),
    columnHelper.display( {
      id: 'cadence',
      header: 'Run Schedule',
      cell: info => formatCronCadence( info.row.original.schedule.cron_expression ),
      meta: { classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } ) }
    } ),
    columnHelper.display( {
      id: 'nextRun',
      header: 'Next Run',
      cell: info => formatNextRun( info.row.original, useLocalZone ),
      meta: { classStr: buildColumnClassString( { width: 20 } ) }
    } ),
    columnHelper.display( {
      id: 'lastRun',
      header: 'Last Run',
      cell: info => {
        const lastFireAt = info.row.original.details?.last_fire_at;
        if ( !lastFireAt ) return <span className={ styles.muted }>—</span>;
        return (
          <TimeFormatter
            sourceType={ TimeSourceTypeEnum.ISO }
            value={ lastFireAt }
            opts={ { useLocalZone } }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
          />
        );
      },
      meta: { classStr: buildColumnClassString( { width: 15 } ) }
    } ),
    columnHelper.display( {
      id: 'createdAt',
      header: 'Created',
      cell: info => (
        <TimeFormatter
          sourceType={ TimeSourceTypeEnum.ISO }
          value={ info.row.original.schedule.created_at }
          opts={ { useLocalZone } }
          outputFormat={ OutputFormatEnum.STRING_SINGLE }
        />
      ),
      meta: { classStr: buildColumnClassString( { width: 10 } ) }
    } )
  ], [ sourcesById, expanded, useLocalZone ] );

  const table = useNativeTable<ScheduleWithDetails>( {
    data: schedules?.items ?? [],
    columns
  } );

  const renderSubRow = useCallback( ( row: ScheduleWithDetails ) => {
    if ( !expanded.has( row.schedule.id ) ) return null;
    const source = sourcesById.get( row.schedule.source_id );
    return (
      <div className={ styles.detail_panel } data-testid={ `schedule-detail-${ row.schedule.id }` }>
        <div className={ styles.detail_item }>
          <span className={ styles.detail_label }>Source Owners:</span>
          <span>{ displayUser( source?.created_by, usersMap ) }</span>
        </div>
        <div className={ styles.detail_item }>
          <span className={ styles.detail_label }>Evaluation scheduled by:</span>
          <span>{ displayUser( row.schedule.created_by, usersMap ) }</span>
        </div>
        <div className={ styles.detail_item }>
          <span className={ styles.detail_label }>Last Run:</span>
          <span>
            { row.details?.last_fire_at ? (
              row.details?.last_run_id ? (
                <Link
                  to={ `/evaluations/results?eid=${ evaluationId }&rid=${ row.details.last_run_id }` }
                  className={ styles.detail_link }
                >
                  <TimeFormatter
                    sourceType={ TimeSourceTypeEnum.ISO }
                    value={ row.details.last_fire_at }
                    opts={ { useLocalZone } }
                    outputFormat={ OutputFormatEnum.STRING_SINGLE }
                  />
                </Link>
              ) : (
                <TimeFormatter
                  sourceType={ TimeSourceTypeEnum.ISO }
                  value={ row.details.last_fire_at }
                  opts={ { useLocalZone } }
                  outputFormat={ OutputFormatEnum.STRING_SINGLE }
                />
              )
            ) : '—' }
          </span>
        </div>
        <div className={ styles.detail_item }>
          <span className={ styles.detail_label }>Schedule created on:</span>
          <span>
            <TimeFormatter
              sourceType={ TimeSourceTypeEnum.ISO }
              value={ row.schedule.created_at }
              opts={ { useLocalZone } }
              outputFormat={ OutputFormatEnum.STRING_SINGLE }
            />
          </span>
        </div>
      </div>
    );
  }, [ expanded, sourcesById, usersMap, useLocalZone, evaluationId ] );

  if ( isLoading ) return <Loader />;
  if ( isError ) return <div className={ styles.error } data-testid="schedules-error">Error loading schedules</div>;
  if ( !schedules?.items || schedules.items.length === 0 ) {
    return (
      <div className={ styles.empty } data-testid="schedules-empty">
        No scheduled runs yet. Create one from the &ldquo;Create Run&rdquo; modal.
      </div>
    );
  }

  return (
    <div className={ clsx( styles.layout ) } data-testid="schedules-table-container">
      <NativeTable
        table={ table }
        tbodyTestId="table-body-schedules"
        onRowClick={ ( row ) => toggleExpanded( row.schedule.id ) }
        renderSubRow={ renderSubRow }
      />
    </div>
  );
}
